import { useMemo } from 'react'
import { Box, Typography, TextField } from '@mui/material'
import ScreeningRangeComponent from './questionInputComponents/ScreeningRangeComponent'
import ScreeningSingleChoice from './questionInputComponents/ScreeningSingleChoice'
import ScreeningSingleChoiceWithDescription from './questionInputComponents/ScreeningSingleChoiceWithDescription'
import ScreeningTextBox from './questionInputComponents/ScreeningTextBox'
import styled from 'styled-components'

const QuestionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background: #f8fafc;
  border: 2px solid ${(props) => (props.$hasAnswer ? 'rgba(16, 185, 129, 0.2)' : '#e2e8f0')};
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: ${(props) => (props.$hasAnswer ? 'rgba(16, 185, 129, 0.4)' : '#6779e3')};
    box-shadow: 0 2px 8px rgba(103, 121, 227, 0.1);
  }
`

const QuestionText = styled(Typography)`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
  margin-bottom: 4px;
`

const QuestionTextEnglish = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #64748b;
  font-style: italic;
  margin-top: 4px;
`

const RequiredIndicator = styled.span`
  color: #ef4444;
  margin-left: 4px;
`

const NoteContainer = styled(Box)`
  margin-top: 16px;
  padding: 16px;
  background: rgba(103, 121, 227, 0.04);
  border: 2px solid rgba(103, 121, 227, 0.2);
  border-radius: 12px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #6779e3;
    box-shadow: 0 0 0 3px rgba(103, 121, 227, 0.1);
  }
`

const StyledTextField = styled(TextField)`
  width: 100%;

  .MuiOutlinedInput-root {
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      border-color: #6779e3;
    }

    &.Mui-focused {
      border-color: #6779e3;
      box-shadow: 0 0 0 3px rgba(103, 121, 227, 0.1);
    }
  }

  .MuiOutlinedInput-input {
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.5;
  }
`

export default function QuestionComponent({ question, handleChangeAnswer, getQuestionAnswer, selectedLanguage }) {
  const answer = getQuestionAnswer(question?.id)
  const hasAnswer = !!answer?.response
  const englishQuestionText = question?.questionText
  const translatedQuestionText = question?.translations?.[selectedLanguage]?.questionText

  const InputComponent = useMemo(() => {
    switch (question?.questionType) {
      case "drop_down":
      case 'single_choice':
        return ScreeningSingleChoice
      case 'text':
        return ScreeningTextBox
      case 'single_choice_with_description_text':
        return ScreeningSingleChoiceWithDescription
      case 'range':
        return ScreeningRangeComponent
      default:
        return 'div'
    }
  }, [question?.questionType])

  // Check if this is a non-text question type (should show note text box)
  const isNonTextQuestion = question?.questionType !== 'text'

  // Handle note change
  const handleNoteChange = (e) => {
    const noteValue = e.target.value
    handleChangeAnswer(question?.id, {
      ...(answer || {}),
      additionalResponse: noteValue,
    })
  }

  return (
    <QuestionContainer $hasAnswer={hasAnswer}>
      <Box>
        {selectedLanguage !== 'en' && translatedQuestionText ? (
          <>
            <QuestionText>
              {translatedQuestionText}
              {question?.isRequired && <RequiredIndicator>*</RequiredIndicator>}
            </QuestionText>
            <QuestionTextEnglish>({englishQuestionText})</QuestionTextEnglish>
          </>
        ) : (
          <QuestionText>
            {englishQuestionText}
            {question?.isRequired && <RequiredIndicator>*</RequiredIndicator>}
          </QuestionText>
        )}
      </Box>
      <Box sx={{ mt: 1 }}>
        <InputComponent
          answer={answer}
          question={question}
          onChange={(value) => handleChangeAnswer(question?.id, value)}
          required={question?.isRequired}
        />
      </Box>
      {/* Note text box for all non-text question types */}
      {isNonTextQuestion && (
        <NoteContainer>
          <StyledTextField
            multiline
            rows={3}
            value={answer?.additionalResponse || ''}
            onChange={handleNoteChange}
            placeholder="Add a note (optional)..."
            variant="outlined"
            fullWidth
          />
        </NoteContainer>
      )}
    </QuestionContainer>
  )
}
